import { useEffect, useState } from 'react';
import { getCookie, getConsentCookie, getConsentCookieJSON, getConsentCookieName, getAllSavedCookieNames, getExpireTimeUTCStringForCookies, setConsentChanged, setConsentCookie, SetSandhillsConsentCookie, consentCookieNames, sandhillsRequest } from '@Utilities/CookieHelper';

const _saveSelections = 'saveselections';

const CookiesConsentView = (props) => {
    const {
        IsInTranslationEditorMode,
        Translations,
        Message,
        RequireInternationalConsent,
        CookieSettings,
        Classifications,
        ShowCookieModal,
        CurrentUserIsAuthenticated,
        CurrentTrackingUserID,
        setShowModalParentState
    } = props;

    const [ckySettings, setConsentSettings] = useState(CookieSettings);
    const [showModal, setShowModal] = useState(ShowCookieModal);

    const getTranslation = (key) => {
        return IsInTranslationEditorMode ?
            <span dangerouslySetInnerHTML={{ __html: Translations[key] }}></span> :
            Translations[key];
    };

    const Actions = {
        Accept: "ACCEPT",
        Deny: "DENY",
        Update: "UPDATE"
    };

    const Buttons = [
        {
            Id: "StrictlyNecessary",
            Label: getTranslation("RejectAllExceptStrictlyNecessary"),
            Classification: "StrictlyNecessary"
        },
        {
            Id: "SaveSelections",
            Label: getTranslation("SaveSelections"),
            Classification: "SaveSelections"
        },
        {
            Id: "AcceptAll",
            Label: getTranslation("AcceptAll"),
            Classification: "AcceptAll"
        }
    ];

    const Toggles = [
        {
            Id: "StrictlyNecessary",
            Label: "(" + getTranslation("ON") + ") " + getTranslation("StrictlyNecessaryCookies"),
            Classification: "StrictlyNecessary"
        },
        {
            Id: "Customization",
            Label: getTranslation("PersonalizedCookies"),
            Classification: "Customization"
        },
        {
            Id: "Advertising",
            Label: getTranslation("AdvertisingCookies"),
            Classification: "Advertising"
        },
        {
            Id: "Analytics",
            Label: getTranslation("AnalyticsCookies"),
            Classification: "Analytics"
        }
    ]

    const allowModalBackgroundScroll = () => {
        //allow scrolling when modal is open
        document.body.style.overflow = null
        document.body.style.paddingRight = null;
        document.body.style.position = null;
    }

    useEffect(() => {
        if (props.ShowCookieModal != showModal) {
            setShowModal(props.ShowCookieModal);
        }
    }, [props.ShowCookieModal]); 

    useEffect(() => {
        if (showModal) {
            allowModalBackgroundScroll();

            //Using footer link to open modal.
            //Check if cookie exists, otherwise, reset ckySettings state.   
            if (RequireInternationalConsent) {
                var consentCookie = getConsentCookieJSON();
                if (consentCookie == null) {
                    resetConsentSettings();
                }
            }
        }
    }, [showModal]);

    //on page load, check if user is authenticated and cookie exists
    //if TrackingUserID is not set or different, update cookie with CentralTrackingUserID and save consent preferences to DB
    useEffect(() => {
        setCookiePolicyLinkEvent();
        checkAgreementVersion();

        setConsentChanged(ckySettings, RequireInternationalConsent, Classifications);

        if (RequireInternationalConsent) {
            initSettingsAndCookies();
        }
    }, []);

    const initSettingsAndCookies = () => {
        //If the only cookie that exists is a guest cookie, use the preferences for that cookie and create a new sandhills-consent-[UserID] cookie tied to that logged-in user.
        //If both a guest cookie and a UserID cookie exist, and the person logging in matches that UserID, then apply those preferences and do not prompt the user.
        //If both a guest cookie and a UserID cookie exist, and the person logging in does not match the existing UserID cookie, prompt the user with the cookie modal.
        if (CurrentUserIsAuthenticated) {
            var guestCookie = getConsentCookie(true);
            var usersCookieNames = getAllSavedCookieNames().filter(c => c.includes(consentCookieNames.SandhillsConsent + '-'));
            var authenticatedUserConsent = getConsentCookie(false, CurrentTrackingUserID, CurrentUserIsAuthenticated);

            if (guestCookie) {
                if ((usersCookieNames.length == 0 || (guestCookie.ShouldResync || authenticatedUserConsent == null)) && (!guestCookie.TrackingUserID || guestCookie?.TrackingUserID == ckySettings.TrackingUserID)) {
                    setConsentChanged(guestCookie, RequireInternationalConsent, Classifications);
                    authenticatedUserConsent = guestCookie;
                    if (guestCookie.TrackingUserID) guestCookie.TrackingUserID = ckySettings.TrackingUserID;
                    SetSandhillsConsentCookie(guestCookie, daysToSetCookie(guestCookie.ConsentStatus), guestCookie.TrackingUserID);
                    setConsentSettings(guestCookie);

                    if (RequireInternationalConsent && (guestCookie.ShouldResync || guestCookie?.WebAgreementConsentID <= 0)) {
                        //Don't save unless resync and not tied to WebAgreementConsentID, otherwise we'll create dupes.  
                        saveConsentToDB(guestCookie);
                    }
                }
                else if (RequireInternationalConsent && authenticatedUserConsent?.ShouldResync) {
                    saveConsentToDB(authenticatedUserConsent);
                } else if (authenticatedUserConsent && guestCookie.TrackingUserID != authenticatedUserConsent.TrackingUserID) {
                    //sync guest cookie with authenticated user cookie to persist settings after logging out.  
                    SetSandhillsConsentCookie(authenticatedUserConsent, daysToSetCookie(authenticatedUserConsent.ConsentStatus));
                }
            } else if (authenticatedUserConsent) {
                //guest cookie isn't set, create it.   
                SetSandhillsConsentCookie(authenticatedUserConsent, daysToSetCookie(authenticatedUserConsent.ConsentStatus));
            }

            if (authenticatedUserConsent == null) {
                setConsentChanged(ckySettings, RequireInternationalConsent, Classifications);
                setShowModal(true);
            }
        }
    }

    const setCookiePolicyLinkEvent = () => {
        const hereLink = document.getElementById('open-consent-modal');
        if (hereLink) {
            hereLink.onclick = function (e) {
                e.preventDefault();
                setShowModal(true);
                setShowModalParentState(true);
            };
        }
    }

    const checkAgreementVersion = () => {
        var consentCookie = getConsentCookie(false, CurrentTrackingUserID, CurrentUserIsAuthenticated);
        var shouldReset = true;

        if (ckySettings?.TrackingUserID > 0 && CurrentUserIsAuthenticated) {
            var guestCookie = getConsentCookie(true);
            if (guestCookie?.TrackingUserID == consentCookie?.TrackingUserID &&
                guestCookie?.WebAgreementID == ckySettings?.WebAgreementID &&
                consentCookie?.WebAgreementID != ckySettings?.WebAgreementID) {
                SetSandhillsConsentCookie(guestCookie, daysToSetCookie(guestCookie.ConsentStatus), guestCookie.TrackingUserID);
                shouldReset = false;
            }
        }

        if (consentCookie && shouldReset && consentCookie.WebAgreementID != ckySettings.WebAgreementID) {
            document.cookie = getConsentCookieName(false, CurrentTrackingUserID, CurrentUserIsAuthenticated) + '=; ' + 'expires=' + getExpireTimeUTCStringForCookies(-1) + '; path=/;';
            document.cookie = getConsentCookieName(true) + '=; ' + 'expires=' + getExpireTimeUTCStringForCookies(-1) + '; path=/;';
            resetConsentSettings();
            setShowModal(true);
            setShowModalParentState(true);
        }
    }

    const onSubmit = (classification, isAccepting) => {
        var consentCookie = getConsentCookieJSON(false, CurrentTrackingUserID, CurrentUserIsAuthenticated);

        //Don't run if they're just closing the modal and there's existing consent.   
        if (!consentCookie || (consentCookie && isAccepting)) {
            isAccepting = !RequireInternationalConsent || isAccepting;

            setConsentSettings(prvCkySettings => {
                //update ckySettings properties then save to cookies
                const newSettings = {
                    ...prvCkySettings,
                    ConsentStatus: getStatus(isAccepting, prvCkySettings.ConsentStatus),
                    ActionDateTime: new Date().toISOString(),
                    ShouldResync: RequireInternationalConsent
                };

                if (RequireInternationalConsent && !(classification?.toUpperCase() === _saveSelections?.toUpperCase())) {
                    Classifications.forEach(c => {
                        if (!(classification?.toUpperCase() === c?.toUpperCase() || c?.toUpperCase() === "StrictlyNecessary".toUpperCase())) {
                            newSettings[c] = !(classification?.toUpperCase() === "StrictlyNecessary".toUpperCase());
                        }
                    });
                }

                SetSandhillsConsentCookie(newSettings, daysToSetCookie(newSettings.ConsentStatus));
                if (RequireInternationalConsent) {
                    if (CurrentUserIsAuthenticated && prvCkySettings?.TrackingUserID > 0) {
                        SetSandhillsConsentCookie(newSettings, daysToSetCookie(newSettings.ConsentStatus), prvCkySettings.TrackingUserID);
                    }
                    setConsentChanged(newSettings, RequireInternationalConsent, Classifications);
                    saveConsentToDB(newSettings);
                }

                return newSettings;
            });
        }
        setShowModal(false);
        setShowModalParentState(false);
    }

    const saveConsentToDB = (consentSettings) => {
        if (RequireInternationalConsent && CurrentUserIsAuthenticated) {
            sandhillsRequest({
                url: '/listinginput/ajax/SaveCookieConsent',
                method: 'post',
                data: { consentSettings: consentSettings },
                errorHandling: {
                    message: 'Error Saving Cookie Preferences.',
                    showDetails: true,
                    useConsole: true,
                    useAlert: false
                },
                callback: function (response) {
                    if (response) {
                        //if successful, update WebAgreementConsentID 
                        setConsentSettings(prvCkySettings => {
                            if (response.Success && response.WebAgreementConsentID && response.WebAgreementConsentID > 0) {
                                prvCkySettings.WebAgreementConsentID = response.WebAgreementConsentID
                            }

                            prvCkySettings.ShouldResync = response.ShouldResync;
                            prvCkySettings.Success = response.Success;
                            prvCkySettings.ErrorCode = response.ErrorCode;

                            SetSandhillsConsentCookie(response, daysToSetCookie(prvCkySettings.ConsentStatus));

                            if (response.TrackingUserID && response.TrackingUserID > 0) {
                                SetSandhillsConsentCookie(response, daysToSetCookie(prvCkySettings.ConsentStatus), response.TrackingUserID);
                            }

                            return prvCkySettings;
                        });
                    }
                }
            });
        }
    }

    const onChange = (e, classification) => {
        if (!(classification?.toUpperCase() === null)) {
            setConsentSettings(prvCkySettings => ({ ...prvCkySettings, [classification]: e }))
        }
    }

    //if for some reason user deletes cookie and reopens the modal,
    //we need to reset the ckySettings state
    const resetConsentSettings = () => {
        setConsentSettings(prvCkySettings => {
            const newSettings = {
                ...prvCkySettings,
                ConsentStatus: Actions.Deny,
                ActionDateTime: null,
                ShouldResync: false
            };

            Classifications.forEach(c => {
                newSettings[c] = c?.toUpperCase() === "StrictlyNecessary".toUpperCase();
            });

            return newSettings;
        });
    }

    const getStatus = (Accept, currentStatus) => {
        var status = currentStatus;
        if (Accept) {
            if (currentStatus === Actions.Deny) {
                status = Actions.Accept;
            } else if (currentStatus === Actions.Accept || currentStatus === Actions.Update) {
                status = Actions.Update;
            } else {
                status = Actions.Deny;
            }
        }
        return status
    }

    const daysToSetCookie = (status) => {
        var days = 400;
        if (RequireInternationalConsent && (status == Actions.Deny)) {
            days = 30;
        }
        return days;
    }

    const getLabel = (classification, label) => {

        if (classification?.toUpperCase() === "StrictlyNecessary".toUpperCase()) {
            return (<span dangerouslySetInnerHTML={{ __html: label }} />);
        }

        return label;
    }

    const toggleChange = (e,t) => {
        if (e.target.className.includes('switch')) {
            e.target.checked = ckySettings[t.Classification];
        }
        if (onChange) {
            onChange(e.target.checked, t.Classification);
        }
    }

    return (
        <>
            {(showModal) &&
            (<div className={'ts-modal-container cookies-consent'} role='dialog' aria-modal='true'>
                <div className={'ts-modal-content'}>
                    <div className={'ts-modal-header des-modal-header'}>
                    <button type='button' className='close ts-button' aria-label={getTranslation('Close')} onClick={() => onSubmit("StrictlyNecessary", false)}>
                            <i className={'fa-regular fa-circle-xmark'}></i>
                        </button>
                        <div className='cookies-inner'>
                            <span dangerouslySetInnerHTML={{ __html: Message }}></span>
                        </div>
                    </div>

                    {RequireInternationalConsent &&
                    <>
                        <div className={'ts-modal-body des-modal-body toggle-container'}>
                            {Toggles.map(t =>
                                <div key={t.Id+"-toggle-key"} className={'toggle-label' + (t.Classification?.toUpperCase() === "StrictlyNecessary".toUpperCase() ? ' shouldDisable' : '')}>
                                    {t.Label ?
                                        <label>
                                            <div className={'toggle' + (t.Classification?.toUpperCase() === "StrictlyNecessary".toUpperCase() ? ' hide' : '')} onClick={(e) => toggleChange(e,t)} onKeyDown={(e) => { if (e.key === 'Enter') toggleChange(e,t) }} >
                                                <input id={t.Id + '-toggle'} type='checkbox' defaultChecked={ckySettings[t.Classification]} />
                                                <div className={'toggle__switch' + (ckySettings[t.Classification] ? ' toggle__switch_checked' : '')}></div>
                                                <div className={'toggle__track' + (ckySettings[t.Classification] ? ' toggle__track_checked' : '')}></div>
                                            </div>
                                            <span>{getLabel(t.Classification, t.Label)}</span>
                                        </label> :
                                        <>
                                            {getLabel(t.Classification, t.Label) && <label htmlFor={t.Id + '-toggle'}> {getLabel(t.Classification, t.Label)}</label>}
                                            <div className={'toggle' + (t.Classification?.toUpperCase() === "StrictlyNecessary".toUpperCase() ? ' hide' : '')} onClick={(e) => toggleChange(e, t)} onKeyDown={(e) => { if (e.key === 'Enter') toggleChange(e, t) }} >
                                                <input id={t.Id + '-toggle'} type='checkbox' defaultChecked={ckySettings[t.Classification]} />
                                                <div className={'toggle__switch' + (ckySettings[t.Classification] ? ' toggle__switch_checked' : '')}></div>
                                                <div className={'toggle__track' + (ckySettings[t.Classification] ? ' toggle__track_checked' : '')}></div>
                                            </div>
                                        </>
                                    }
                                </div>
                            )}
                        </div>
                        <div className="btn-container">
                        {Buttons.map((b) => <button key={b.Id+"-button-key"} className='btn' key={b.Id} id={b.Id} onClick={() => onSubmit(b.Classification, true)}>{b.Label}</button>)}
                        </div>                     
                    </>}
                </div>
            </div>)}
        </>
    )
};

export default CookiesConsentView;
global.CookiesConsentView = CookiesConsentView;